/**
 * Zuivere regels voor de actiebalk met een Tab-groep (UX-besluiten D2/D3 en Q2, 20 oktober 2026; herzien met
 * D4/D5 van 27 oktober 2026, #2831).
 *
 * De Tab-groep is sinds 27 oktober één icoonknop (geen tabnaam of aantal in de balk; die staan in tooltip en
 * toegankelijke naam). Alle groepen staan links, in de volgorde Tab · Uitvoeren · Opvolgen · Filter. Een groep
 * klapt pas in als de balk echt te smal is, dus als de groepen samen breder zijn dan de rij:
 *  - in de volgorde Filter (alleen als die meer dan één knop heeft), Uitvoeren, Opvolgen;
 *  - zodra alles past, stopt het inklappen.
 * Een ingeklapte groep wordt één knop met ▾ die een menu met de knoppen van die groep opent; een groep verdwijnt
 * nooit helemaal. Geen DOM: de aanroeper meet en geeft de breedtes mee (engine-tests).
 */

export interface TabBarGroupSpec {
  /** Vaste naam van de groep: "filter" | "run" | "follow" (of een eigen naam). */
  id: string;
  /** Breedte van de groep uitgeklapt, inclusief scheiding en marge. */
  fullWidth: number;
  /** Breedte van de groep ingeklapt (één knop met ▾). */
  miniWidth: number;
  /** Aantal knoppen; met één knop klapt een groep niet in (ingeklapt zou hij even breed zijn). */
  buttons: number;
}

export interface TabBarPlan {
  /** Groepen die ingeklapt moeten worden, in de volgorde waarin dat gebeurde. */
  collapsed: string[];
  /** Breedte die na de groepen in de rij overblijft (negatief als zelfs alles ingeklapt niet past). */
  spare: number;
}

/** Volgorde van inklappen (besluit D3): Filter, dan Uitvoeren, dan Opvolgen; andere groepen daarna. */
const COLLAPSE_ORDER: Record<string, number> = { filter: 0, run: 1, follow: 2 };

/**
 * De groepen die mogen inklappen, in de volgorde van D3. Filter alleen met meer dan één knop; een
 * groep met één knop klapt nooit in (ingeklapt is hij net zo breed).
 */
export function collapseOrder(groups: TabBarGroupSpec[]): TabBarGroupSpec[] {
  return groups
    .filter((g) => g.buttons > 1)
    .sort((a, b) => (COLLAPSE_ORDER[a.id] ?? 9) - (COLLAPSE_ORDER[b.id] ?? 9));
}

/**
 * Bepaalt welke groepen inklappen bij een gegeven balkbreedte.
 *
 * @param available  breedte van de rij (binnen de marges)
 * @param tabWidth   breedte van de Tab-groep (één icoonknop, vast)
 * @param groups     de andere groepen, in de volgorde van de balk
 */
export function planTabBar(
  available: number,
  tabWidth: number,
  groups: TabBarGroupSpec[]
): TabBarPlan {
  const collapsed: string[] = [];
  const widthOf = (g: TabBarGroupSpec): number =>
    collapsed.includes(g.id) ? g.miniWidth : g.fullWidth;
  const spare = (): number => available - tabWidth - groups.reduce((n, g) => n + widthOf(g), 0);
  for (const g of collapseOrder(groups)) {
    if (spare() >= 0) break;
    collapsed.push(g.id);
  }
  return { collapsed, spare: spare() };
}
